<template>
  <div class="table-responsive table-hover">
    <table class="table permission-matrix" style="width:100%">
      <thead>
        <tr class="permission-matrix-row">
          <th>Permission</th>
          <th class="permission-matrix-access-cell">Create</th>
          <th class="permission-matrix-access-cell">Read</th>
          <th class="permission-matrix-access-cell">Update</th>
          <th class="permission-matrix-access-cell">Delete</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(category, index) in categoryList">
          <tr :key="index" class="permission-matrix-row-header">
            <td class="text-capitalize font-bold permission-matrix-category-clickable" @click="categoryBarClicked($event, category, true)">
              <i class="far fa-angle-right" v-if="!category.expanded"> </i>
              <i class="far fa-angle-down" v-if="category.expanded"> </i>
              {{category.name}}
            </td>
            <td class="permission-matrix-access-data permission-matrix-category-clickable" @click="categoryBarClicked($event, category, false)">
              <PermissionMatrixCell
                :code="category.name"
                access="create"
                :header="true"
                :mapData="category.create"
                :editing="editing && category.create.editable"
                :editMode="editMode"
                @update="onCategoryPermissionUpdate($event, category.name, 'create')"
                v-if="category.create.available"
              />
              <div class="permission-matrix-category-data-title" v-if="editing && category.create.available">create</div>
            </td>
            <td class="permission-matrix-access-data permission-matrix-category-clickable" @click="categoryBarClicked($event, category, false)">
              <PermissionMatrixCell
                :code="category.name"
                access="read"
                :header="true"
                :mapData="category.read"
                :editing="editing && category.read.editable"
                :editMode="editMode"
                @update="onCategoryPermissionUpdate($event, category.name, 'read')"
                v-if="category.read.available"
              />
              <div class="permission-matrix-category-data-title" v-if="editing && category.read.available">read</div>
            </td>
            <td class="permission-matrix-access-data permission-matrix-category-clickable" @click="categoryBarClicked($event, category, false)">
              <PermissionMatrixCell
                :code="category.name"
                access="update"
                :header="true"
                :mapData="category.update"
                :editing="editing && category.update.editable"
                :editMode="editMode"
                @update="onCategoryPermissionUpdate($event, category.name, 'update')"
                v-if="category.update.available"
              />
              <div class="permission-matrix-category-data-title" v-if="editing && category.update.available">update</div>
            </td>
            <td class="permission-matrix-access-data permission-matrix-category-clickable" @click="categoryBarClicked($event, category, false)">
              <PermissionMatrixCell
                :code="category.name"
                access="delete"
                :header="true"
                :mapData="category.delete"
                :editing="editing && category.delete.editable"
                :editMode="editMode"
                @update="onCategoryPermissionUpdate($event, category.name, 'delete')"
                v-if="category.delete.available"
              />
              <div class="permission-matrix-category-data-title" v-if="editing && category.delete.available">delete</div>
            </td>
          </tr>
          
          <tr v-for="(permission) in category.permissions" :key="permission.name" v-show="category.expanded">
            <td class="permission-matrix-no-border">
              &nbsp;&nbsp;&nbsp;
              <a @click.prevent="displayPermissionPopover($event, permission)"><i class="far fa-question-circle"></i></a>
              &nbsp;
              {{permission.name}}
            </td>
            <td class="permission-matrix-access-data permission-matrix-no-border">
              <PermissionMatrixCell
                :code="permission.code"
                access="create"
                :mapData="permission.create"
                :editing="editing && permission.create.editable"
                :editMode="editMode"
                @update="onPermissionStateUpdated($event, permission.code, 'create')"
                v-if="permission.create.available"
              />
            </td>
            <td class="permission-matrix-access-data permission-matrix-no-border">
              <PermissionMatrixCell
                :code="permission.code"
                access="read"
                :mapData="permission.read"
                :editing="editing && permission.read.editable"
                :editMode="editMode"
                @update="onPermissionStateUpdated($event, permission.code, 'read')"
                v-if="permission.read.available"
              />
            </td>
            <td class="permission-matrix-access-data permission-matrix-no-border">
              <PermissionMatrixCell
                :code="permission.code"
                access="update"
                :mapData="permission.update"
                :editing="editing && permission.update.editable"
                :editMode="editMode"
                @update="onPermissionStateUpdated($event, permission.code, 'update')"
                v-if="permission.update.available"
              />
            </td>
            <td class="permission-matrix-access-data permission-matrix-no-border">
              <PermissionMatrixCell
                :code="permission.code"
                access="delete"
                :mapData="permission.delete"
                :editing="editing && permission.delete.editable"
                :editMode="editMode"
                @update="onPermissionStateUpdated($event, permission.code, 'delete')"
                v-if="permission.delete.available"
              />
            </td>
          </tr>
          <tr :key="'spacer' + index" v-show="category.expanded">
            <td class="permission-matrix-no-border">&nbsp;</td>
            <td class="permission-matrix-access-data permission-matrix-no-border">&nbsp;</td>
            <td class="permission-matrix-access-data permission-matrix-no-border">&nbsp;</td>
            <td class="permission-matrix-access-data permission-matrix-no-border">&nbsp;</td>
            <td class="permission-matrix-access-data permission-matrix-no-border">&nbsp;</td>
          </tr>
        </template>
      </tbody>
    </table>

    <HelpPopover ref="permissionHelpPopover" :title="helpSelectedPermission.name">
      <!-- <h4>{{helpSelectedPermission.category}}<i class="far fa-angle-right mx-2"> </i>{{helpSelectedPermission.name}}</h4> -->
      <strong>Permission </strong><br />
      {{helpSelectedPermission.description}}<br />
      <div v-if="permissionHasDependencies(helpSelectedPermission)">
        <br />
        <strong>Dependencies </strong><br />This permission is dependent on the following permissions:<br />
        <ul class="mt-1">
          <li v-for="permission of permissionDependencyList(helpSelectedPermission)" :key="permission">{{permission}}</li>
        </ul>
      </div>

    </HelpPopover>
  </div>
</template>

<style>
.permission-matrix-category-clickable {
  cursor: pointer;
}
.permission-matrix tr {
  height: 32px;
  -webkit-touch-callout: none;
    -webkit-user-select: none;
     -khtml-user-select: none;
       -moz-user-select: none;
        -ms-user-select: none;
            user-select: none;
}
.permission-matrix-access-cell {
  width: 128px;
}
.permission-matrix-access-data {
  width: 64px;
}
.permission-matrix-category-data-title {
  text-align: left;
  font-size: 7px;
  text-transform: uppercase;
  color: #808080;
  margin-bottom: -8px;
  margin-top: 2px;
  margin-left: 8px;
}
.permission-matrix-no-border {
  border: none;
}
.permission-matrix-row-header {
  background-color: rgba(0, 0, 0, 0.04)
}
</style>

<script>
import _ from 'lodash'
import { PermissionStateTypes, PermissionAccessTypes } from '@/store/types'
import PermissionMatrixCell from '@/modules/users/components/permissionMatrixCell.vue'

export default {
  name: "PermissionMatrix",
  components: {
    PermissionMatrixCell
  },
  props: {
    permissionMap: Object,
    editMode: String,
    editing: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    permissionMap: {
      immediate: true,
      handler: function (newVal, oldVal) {
        this.currentPermissionMap = _.cloneDeep(newVal)
        this.generateCategoryList()
      },
      deep: true
    },
  },
  data: () => {
    return {
      currentPermissionMap: {},
      categoryList: [],
      helpSelectedPermission: {}
    }
  },
  computed: {
    isUserMode() {
      return this.editMode == 'user'
    },
    isGroupMode() {
      return this.editMode == 'group'
    },
  },
  mounted() {
    this.categoryList = []
  },
  methods: {
    categoryBarClicked(evt, category, nameColumn) {
      // Ignore clicks on permission switches
      if($(evt.target).is(':input') || $(evt.target).is('label'))
        return true

      category.expanded = !category.expanded
      return true
    },
    onPermissionStateUpdated(state, code, access) {
      this.currentPermissionMap[code][access] = state

      this.updatePermissionDependencies(code, access)
      this.updateCategoryStates()

      this.$emit('update', this.currentPermissionMap)
    },
    onCategoryPermissionUpdate(state, name, access) {
      var curCat = this.$data.categoryList.filter(cat => { return cat.name == name })[0]
      if(curCat == null)
        return

      curCat.permissions.forEach(permission => {
        if(this.isUserMode) {
          permission[access].userState = state.userState
          permission[access].result = permission[access].userState
        } else {
          permission[access].groupState = state.groupState
          permission[access].result = state.groupState    // TODO: Review this when editing group permissions
        }

        this.updatePermissionDependencies(permission.code, access)
      })

      // Update categories if any dependencies changed
      this.updateCategoryStates()

      this.$emit('update', this.currentPermissionMap)
    },
    updatePermissionDependencies(code, access, visitedList = []) {
      let accessList = Object.values(PermissionAccessTypes)

      var state = null
      if(this.isUserMode)
        state = this.currentPermissionMap[code][access].userState
      else
        state = this.currentPermissionMap[code][access].groupState

      // Ignore codes already visited
      let visitedHash = `${code}-${access}`
      if(visitedList.indexOf(visitedHash) != -1)
        return
      visitedList.push(visitedHash)

      if(state == PermissionStateTypes.allow) {
        // Check dependencies
        if(_.isArray(this.currentPermissionMap[code].dependencyList)) {
          this.currentPermissionMap[code].dependencyList.forEach(dependentCode => {

            this.setPermissionState(dependentCode, PermissionAccessTypes.read, PermissionStateTypes.allow)
            this.updatePermissionDependencies(dependentCode, PermissionAccessTypes.read, visitedList)
          })
        }
        
        // Force Read access if Create/Update/Delete permissions are set
        if(access != PermissionAccessTypes.read) {
          this.setPermissionState(code, PermissionAccessTypes.read, PermissionStateTypes.allow)
        }
      } else if(state == PermissionStateTypes.deny) {
        // If Read access is denied, force Create/Update/Delete permissions to also be denied
        if(access == PermissionAccessTypes.read) {
          accessList.forEach(accessType => {
            if(accessType == PermissionAccessTypes.read)
              return

            this.setPermissionState(code, accessType, PermissionStateTypes.deny)
            this.updatePermissionDependencies(code, accessType, visitedList)
          })
        }

        // If any permissions depend on this permission being allowed, deny those
        Object.keys(this.currentPermissionMap).forEach(permCode => {
          let permission = this.currentPermissionMap[permCode]
          
          if(_.isArray(permission.dependencyList) && permission.dependencyList.indexOf(code) != -1) {

            accessList.forEach(accessType => {
              this.setPermissionState(permission.code, accessType, PermissionStateTypes.deny)
              this.updatePermissionDependencies(permission.code, accessType, visitedList)
            })            
          }
        })
      }
    },
    setPermissionState(code, accessType, state) {
      if(this.isUserMode) {
        this.currentPermissionMap[code][accessType].userState = state
        this.currentPermissionMap[code][accessType].result = this.currentPermissionMap[code][accessType].userState
      } else {
        this.currentPermissionMap[code][accessType].groupState = state
        this.currentPermissionMap[code][accessType].result = this.currentPermissionMap[code][accessType].groupState
      }
    },
    generateCategoryList() {
      if(this.currentPermissionMap == null)
        return

      Object.keys(this.currentPermissionMap).forEach(permKey => {
        let permission = this.currentPermissionMap[permKey]

        if(!permission.available)
          return

        var curCat = this.$data.categoryList.filter(cat => { return cat.name == permission.category })[0]
        if(curCat != null) {
          var curPerm = curCat.permissions.filter(perm => { return perm.code == permission.code })[0]
          if(curPerm != null) {
            curPerm.create = permission.create
            curPerm.read = permission.read
            curPerm.update = permission.update
            curPerm.delete = permission.delete
          } else 
            curCat.permissions.push(permission)
        } else {
          this.$data.categoryList.push({
            name: permission.category,
            expanded: false,
            permissions: [permission],
            create: {
              userState: PermissionStateTypes.mixed,
              groupState: PermissionStateTypes.mixed,
              available: false
            },
            read: {
              userState: PermissionStateTypes.mixed,
              groupState: PermissionStateTypes.mixed,
              available: false
            },
            update: {
              userState: PermissionStateTypes.mixed,
              groupState: PermissionStateTypes.mixed,
              available: false
            },
            delete: {
              userState: PermissionStateTypes.mixed,
              groupState: PermissionStateTypes.mixed,
              available: false
            }
          })
        }
      })
      this.updateCategoryStates()
    },
    updateCategoryStates() {
      let accessTypeList = ['create', 'read', 'update', 'delete']

      this.$data.categoryList.forEach(category => {
        accessTypeList.forEach(accessType => {

          category[accessType].editable = false
          category[accessType].available = false

          var allowCount = 0
          var denyCount = 0

          Object.keys(this.currentPermissionMap).forEach(permKey => {
            let permission = this.currentPermissionMap[permKey]
            if(permission.category != category.name)
              return
            if(!permission.available)
              return
            if(permission[accessType] == null || !permission[accessType].available)
              return

            if(permission[accessType].editable)
              category[accessType].editable = true

            category[accessType].available = true

            if(this.isUserMode) {
              if(permission[accessType].userState == PermissionStateTypes.allow)
                allowCount++
              else if(permission[accessType].userState == PermissionStateTypes.deny)
                denyCount++
              else if(permission[accessType].userState == PermissionStateTypes.inherit) {
                if(permission[accessType].groupState == PermissionStateTypes.allow)
                  allowCount++
                else
                  denyCount++
              }              
            } else {
              if(permission[accessType].groupState == PermissionStateTypes.allow)
                allowCount++
              else
                denyCount++
            }
          })

          var state = PermissionStateTypes.deny
          if(allowCount > 0 && denyCount > 0) {
            state = PermissionStateTypes.mixed
          } else if(allowCount > 0 && denyCount == 0) {
            state = PermissionStateTypes.allow
          }

          if(this.isUserMode) {
            var catState = category[accessType]
            catState.userState = state
            category[accessType] = catState
          } else
            category[accessType].groupState = state
        })
      })
    },
    permissionHasDependencies(permission) {
      if(permission.dependencyList && permission.dependencyList.length > 0)
        return true
      return false
    },
    permissionDependencyList(permission) {
      if(!permission.dependencyList)
        return []

      return permission.dependencyList.map(code => {
        if(this.currentPermissionMap && this.currentPermissionMap[code])
          return this.currentPermissionMap[code].name
        return ''
      })
    },
    permissionDependencyLabel(permission) {
      var dependList = this.permissionDependencyList(permission).join(', ')

      return 'This permission is dependent on the following permissions: ' + dependList
    },
    displayPermissionPopover(ev, permission) {
      this.helpSelectedPermission = _.cloneDeep(permission)
      this.$refs.permissionHelpPopover.displayFromElement(ev.target)
    }
  }
};
</script>